import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type ActiveCall as ActiveCallData,
  ApiError,
  acceptCall,
  acknowledgeProtocol,
  type ConsoleState,
  type CurrentUser,
  callMedia,
  consoleState,
  crisisProtocol,
  declineCall,
  endCall,
  endShift,
  type IncomingCall as IncomingCallData,
  incomingCalls,
  type MediaGrant,
  notificationSettings,
  type PresenceStatus,
  type ProtocolState,
  setPresence,
  startShift,
  testNotification,
} from '../api.ts';
import { ActiveCall } from '../components/ActiveCall.tsx';
import { Announcer } from '../components/Announcer.tsx';
import { Debrief } from '../components/Debrief.tsx';
import { IncomingCall } from '../components/IncomingCall.tsx';
import { InfoTip } from '../components/InfoTip.tsx';
import { PresencePicker } from '../components/PresencePicker.tsx';
import { SupervisionPanel } from '../components/SupervisionPanel.tsx';
import { formatTime, formatWeekdayTime } from '../format.ts';
import { apiMessage } from '../messages.ts';
import { testMessage } from '../notify-text.ts';
import { preferenceEnabled, setPreference } from '../preferences.ts';
import { type PushState, pushStateFrom } from '../push.ts';
import { armAutoUnlock, soundEnabled, startRinging, stopRinging, unlockSound } from '../sound.ts';
import { releaseWakeLock, requestWakeLock } from '../wake-lock.ts';

/**
 * La console di turno.
 *
 * "La schermata su cui il prodotto vive o muore" (mock 2). Tre cose, in
 * quest'ordine di importanza: lo stato, la chiamata che arriva, la chiamata in
 * corso. Tutto il resto viene dopo, e in questa fase non c'è.
 *
 * Dalla fase 4 la chiamata si annuncia da sé: una push arriva anche a scheda
 * chiusa, il suono squilla quando è aperta, e lo schermo resta acceso per il
 * turno. L'interrogazione periodica **resta**, e non è un residuo: una notifica
 * che non arriva non deve significare una chiamata persa
 * (`docs/DECISIONI.md`, O4).
 *
 * Scorciatoie da tastiera per rispondere e chiudere: "durante una chiamata non
 * si naviga per tab" (sezione 7bis).
 */

const CONSOLE_POLL_MS = 5000;
const CALLS_POLL_MS = 2000;

type Props = {
  user: CurrentUser;
  /** Il titolo che riceve il fuoco quando si arriva qui dalla navigazione. */
  pageHeadingRef?: React.RefObject<HTMLHeadingElement | null>;
  /** Porta alla schermata delle notifiche quando da qui non sono attive. */
  onOpenNotifications?: () => void;
};

export function ConsolePage({ user, pageHeadingRef, onOpenNotifications }: Props) {
  const { t, i18n } = useTranslation();

  const [state, setState] = useState<ConsoleState>();
  const [incoming, setIncoming] = useState<IncomingCallData | null>(null);
  const [active, setActive] = useState<ActiveCallData | null>(null);
  const [media, setMedia] = useState<MediaGrant>();
  const [notice, setNotice] = useState<string>();
  /** Ciò che va detto a chi non guarda lo schermo, senza interrompere. */
  const [announcement, setAnnouncement] = useState({ text: '', seq: 0 });
  const [busy, setBusy] = useState(false);
  /** Come sei avvisato su **questo** dispositivo, per dirlo prima del turno. */
  const [pushState, setPushState] = useState<PushState>();
  /**
   * La conversazione appena finita, in attesa del debriefing (sezione 5.8).
   *
   * Facoltativo e a un tocco dall'uscita: da qui si torna in ascolto o si
   * chiude il turno senza spiegazioni, anche senza compilare niente.
   */
  const [debriefQueue, setDebriefQueue] = useState<string[]>([]);
  /** Il canale di testo con la supervisione, quando è aperto (sezione 5.9). */
  const [escalating, setEscalating] = useState(false);
  /**
   * Le scorciatoie, accese o spente, **lette qui**.
   *
   * L'interruttore vive nella schermata delle notifiche, ma l'effetto si vede
   * qui: chi non trova più la riga "Maiusc+A rispondi" non ha modo di sapere
   * dove riaccenderla, e cercarla in una schermata che parla di push non viene
   * in mente a nessuno. Adesso si spegne e si riaccende da dove se ne vede
   * l'effetto, e resta una riga sola — non una schermata nuova.
   *
   * È uno stato di React e non una lettura diretta della preferenza perché il
   * disegno deve cambiare al tocco: `preferenceEnabled` legge
   * `localStorage`, che non fa rieseguire niente.
   */
  const [shortcuts, setShortcuts] = useState(() => preferenceEnabled('console.shortcuts'));

  /**
   * Il debriefing da compilare, e quelli **rimasti indietro**.
   *
   * Era uno solo. Una conversazione finisce, il debriefing compare, e mentre
   * lo si sta compilando arriva un'altra chiamata: la schermata si sostituisce
   * — deve farlo, chi chiama non aspetta — e alla fine della seconda
   * conversazione `setDebriefFor` scriveva sopra la prima. Il debriefing di
   * quella conversazione non era più raggiungibile da nessuna schermata: né
   * lì, né altrove. Il server invece lo accetta ancora — una nota si scrive su
   * qualunque sessione conclusa che sia la propria (`staff-debrief.ts`) —
   * quindi a perderlo era solo la console.
   *
   * Adesso è una coda, in ordine di chiusura, e si compila dalla **più
   * vecchia**: è quella che rischia di essere dimenticata, ed è quella che
   * l'interruzione ha messo da parte.
   */
  const debriefFor = debriefQueue[0];
  const queueDebrief = useCallback((sessionId: string): void => {
    setDebriefQueue((current) =>
      // Una conversazione sola, una volta sola: `hangUp` e l'interrogazione
      // periodica notano la stessa chiusura, e la noterebbero due volte.
      current.includes(sessionId) ? current : [...current, sessionId],
    );
  }, []);
  /**
   * Quelli già visti in questa scheda: scritti, oppure saltati con
   * "compila dopo"/"salta e chiudi il turno".
   *
   * `pendingNotesFor` (server) non sa distinguere le due cose da un
   * riavvio del browser — solo `POST /note` fa sparire una nota per
   * sempre. Senza questo insieme, ogni giro di `refresh()` rimetterebbe in
   * coda una nota appena saltata: "facoltativo" tornerebbe obbligatorio a
   * forza di richiederlo. Vive solo in questa scheda, non in
   * `localStorage`: a un accesso nuovo una nota saltata la sera prima
   * ricompare una volta, ed è il comportamento giusto — non è stata
   * scritta, e "facoltativo" non vuol dire "mai più proposto".
   */
  const dismissedNotes = useRef<Set<string>>(new Set());
  const dropDebrief = useCallback((sessionId: string): void => {
    dismissedNotes.current.add(sessionId);
    setDebriefQueue((current) => current.filter((entry) => entry !== sessionId));
  }, []);
  /**
   * Il protocollo di crisi e la presa visione.
   *
   * "Un volontario senza ack sulla versione corrente non entra in turno"
   * (sezione 5.9): il server lo impone, e questa schermata lo dice **prima** di
   * far premere il pulsante che fallirebbe.
   */
  const [protocol, setProtocol] = useState<ProtocolState>();
  const activeRef = useRef<ActiveCallData | null>(null);
  /**
   * A quale "giro" appartiene ciò che l'interrogazione periodica sta per
   * scrivere.
   *
   * `poll()` e `answer()`/`decline()`/`hangUp()` scrivono negli **stessi** due
   * pezzi di stato — `incoming` e `active` — ma solo le seconde sanno cosa è
   * appena successo: le prime raccontano com'era il mondo quando la richiesta
   * è partita. Con una rete lenta dal telefono — che è il dispositivo per cui
   * questa schermata è pensata — la risposta di un giro partito **prima** di
   * rispondere arrivava dopo, diceva ancora `active: null`, e la console ci
   * credeva: `ActiveCall` veniva smontato con la persona in linea,
   * `session.disconnect()` usciva dalla room, veniva annunciato "chiamata
   * conclusa" e messo in coda un debriefing per una sessione ancora aperta.
   * Due secondi dopo il giro successivo rimontava tutto. È esattamente ciò che
   * il vincolo 5 vieta: una sessione attiva non viene mai interrotta.
   * La variante più frequente era più silenziosa e altrettanto sbagliata: dopo
   * "Non posso", una risposta in volo rimetteva `incoming` e lo squillo
   * ripartiva.
   *
   * Il rimedio è quello di `hooks/useLoad.ts`, tradotto in un contatore perché
   * qui non c'è niente da annullare — la richiesta è già tornata, è la sua
   * risposta a non valere più. Ogni azione ne apre uno nuovo **prima** di
   * toccare lo stato e uno ancora **dopo** che il server ha risposto: così
   * cade sia la risposta partita prima dell'azione, sia quella partita mentre
   * l'azione era in volo, che è la finestra in cui il server dice ancora la
   * verità di ieri.
   */
  const generationRef = useRef(0);
  const nextGeneration = useCallback((): void => {
    generationRef.current += 1;
  }, []);
  const mediaRef = useRef<MediaGrant | undefined>(undefined);
  /** Dove torna il fuoco quando il pannello della supervisione si chiude. */
  const escalateButtonRef = useRef<HTMLButtonElement>(null);
  /** Dove torna il fuoco dopo la presa visione del protocollo. */
  const protocolAckRef = useRef<HTMLButtonElement>(null);
  /** Il pulsante del turno: è lì che si stava andando. */
  const shiftButtonRef = useRef<HTMLButtonElement>(null);

  // In un effetto e non nel corpo del render: scrivere su una ref mentre si
  // disegna è un effetto collaterale nel posto sbagliato.
  useEffect(() => {
    activeRef.current = active;
    mediaRef.current = media;
  }, [active, media]);

  /**
   * Chiudere la scheda mentre si è in turno smette di rendere raggiungibile
   * chi sta chiamando, senza che nessuno l'abbia deciso: un tab chiuso per
   * sbaglio non manda mai un "sono in pausa" a chi coordina i turni, né
   * riappende una sessione in corso in modo pulito.
   *
   * Letto da una ref, non da `state.presence` in una dipendenza: il gestore
   * si registra una volta sola, e i browser vogliono comunque il proprio
   * testo generico — `event.returnValue` serve solo a far comparire il loro
   * avviso, non a scriverne uno nostro.
   */
  const presenceRef = useRef<PresenceStatus | undefined>(undefined);
  useEffect(() => {
    presenceRef.current = state?.presence;
  }, [state?.presence]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      if (presenceRef.current && presenceRef.current !== 'offline') {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    globalThis.addEventListener('beforeunload', handleBeforeUnload);
    return () => globalThis.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  /**
   * Il suono pronto per tutto il tempo in cui questa schermata è a schermo,
   * non solo dentro il gestore di un pulsante: vedi `armAutoUnlock` in
   * `sound.ts` per il perché (BJ1/BJ4, `docs/DECISIONI.md`).
   */
  useEffect(() => armAutoUnlock(), []);

  /**
   * Un avviso si vede **e** si sente: lo schermo non è l'unico canale.
   *
   * Il contatore serve a far rileggere **lo stesso** messaggio: chi prova due
   * volte una notifica che non funziona riceve due volte lo stesso esito, e
   * senza questo la seconda volta non sentirebbe niente e non vedrebbe
   * cambiare niente.
   */
  const say = useCallback((text: string) => {
    setNotice(text);
    setAnnouncement((previous) => ({ text, seq: previous.seq + 1 }));
  }, []);

  const refresh = useCallback(async () => {
    try {
      const result = await consoleState();
      setState(result);
      // Debriefing rimasti indietro anche da prima di questa scheda —
      // un'altra scheda chiusa, un crash a metà chiamata. Non quelli già
      // saltati qui: vedi il commento su `dismissedNotes`.
      for (const pending of result.pendingNotes) {
        if (!dismissedNotes.current.has(pending.sessionId)) queueDebrief(pending.sessionId);
      }
    } catch {
      // Una lettura persa non cambia niente: al giro dopo ci riprova.
    }
  }, [queueDebrief]);

  useEffect(() => {
    void refresh();
    const handle = setInterval(() => void refresh(), CONSOLE_POLL_MS);
    return () => clearInterval(handle);
  }, [refresh]);

  useEffect(() => {
    const poll = async (): Promise<void> => {
      // Si legge **prima** della richiesta: è il giro a cui questa risposta
      // appartiene, e se nel frattempo ne è cominciato un altro non conta più.
      const generation = generationRef.current;
      try {
        const result = await incomingCalls();
        if (generation !== generationRef.current) return;
        setIncoming(result.incoming);
        setActive(result.active);
        if (!result.active && activeRef.current) {
          const finished = activeRef.current.sessionId;
          setMedia(undefined);
          setEscalating(false);
          // Il debriefing arriva **dopo**, e non è obbligatorio: la schermata
          // ha due uscite e nessuna delle due chiede di compilare (sezione 5.8).
          queueDebrief(finished);
          say(result.interrupted ? t('call.interrupted') : t('call.ended'));
        }
      } catch {
        // Idem: il polling è la rete di sicurezza, non il punto di rottura.
      }
    };
    void poll();
    const handle = setInterval(() => void poll(), CALLS_POLL_MS);
    return () => clearInterval(handle);
  }, [t, say, queueDebrief]);

  /**
   * Il protocollo di crisi, e se questa persona l'ha già letto.
   *
   * Si legge all'apertura per poterlo **dire prima**: chi non ha la presa
   * visione sulla versione corrente non entra in turno, e scoprirlo premendo
   * "entra in turno adesso" sarebbe scoprirlo nel momento peggiore.
   */
  const refreshProtocol = useCallback(async () => {
    try {
      setProtocol(await crisisProtocol());
    } catch {
      // Il protocollo non si legge: non è una ragione per bloccare la console.
      // Il turno lo rifiuterebbe comunque il server, con il suo messaggio.
    }
  }, []);

  useEffect(() => {
    void refreshProtocol();
  }, [refreshProtocol]);

  /**
   * Come si è avvisati su questo dispositivo.
   *
   * Si legge una volta all'apertura: serve a poterlo **dire** prima del turno,
   * non a decidere qualcosa. Se le notifiche non ci sono, la console funziona
   * comunque — è il polling a farlo — e la riga lo spiega invece di lasciare
   * credere che qualcuno avviserà.
   */
  useEffect(() => {
    void notificationSettings()
      .then((settings) => setPushState(pushStateFrom(settings)))
      .catch(() => setPushState(undefined));
  }, []);

  /**
   * Rientro in una sessione dopo un ricaricamento della pagina.
   * Il token media dura minuti, la conversazione può durare di più: se c'è una
   * sessione attiva e non abbiamo un token, se ne chiede uno nuovo.
   */
  const activeSessionId = active?.sessionId;
  useEffect(() => {
    if (!activeSessionId || mediaRef.current) return;
    // Dipende dall'**id**, non dall'oggetto: il polling ne crea uno nuovo ogni
    // due secondi, e con quello nella lista delle dipendenze un errore qui
    // diventerebbe una richiesta ogni due secondi per tutta la chiamata.
    void callMedia(activeSessionId)
      .then((result) => setMedia(result.media))
      .catch(() => undefined);
  }, [activeSessionId]);

  const answer = useCallback(async () => {
    if (!incoming || busy) return;
    setBusy(true);
    // Da qui in poi ciò che l'interrogazione periodica ha già chiesto non
    // descrive più questa schermata: quel giro è chiuso.
    nextGeneration();
    setNotice(undefined);
    setAnnouncement({ text: '', seq: 0 });
    try {
      const result = await acceptCall(incoming.sessionId);
      if (result.status === 'accepted' && result.media) {
        setMedia(result.media);
        setActive({ sessionId: incoming.sessionId, channel: incoming.channel, startedAt: null });
        setIncoming(null);
      } else {
        // "Ha risposto un collega", non un errore: la corsa fra due
        // accettazioni è normale e la prima vince (sezione 5.5bis).
        say(t(result.status === 'taken' ? 'incoming.taken' : 'incoming.gone'));
        setIncoming(null);
      }
    } catch {
      // Una richiesta fallita non vuol dire che la chiamata non ci sia più:
      // chi sta aspettando è ancora lì finché la proposta non scade.
      say(t('incoming.failed'));
    } finally {
      // E un altro dopo: fra la richiesta e la sua risposta il server diceva
      // ancora che non c'era nessuna conversazione, e chi gliel'ha chiesto in
      // quella finestra sta per riferirlo.
      nextGeneration();
      setBusy(false);
      void refresh();
    }
  }, [busy, incoming, refresh, say, t, nextGeneration]);

  const decline = useCallback(async () => {
    if (!incoming) return;
    const sessionId = incoming.sessionId;
    nextGeneration();
    setIncoming(null);
    await declineCall(sessionId).catch(() => undefined);
    // Il rifiuto ha effetto quando il server lo registra: fino a lì continua a
    // proporre la stessa chiamata, e senza questa riga lo squillo ripartiva da
    // solo addosso a chi aveva appena detto "non posso".
    nextGeneration();
  }, [incoming, nextGeneration]);

  const hangUp = useCallback(async () => {
    const current = activeRef.current;
    if (!current) return;
    nextGeneration();
    setActive(null);
    setMedia(undefined);
    setEscalating(false);
    queueDebrief(current.sessionId);
    await endCall(current.sessionId).catch(() => undefined);
    // Come sopra, dal lato opposto: una risposta partita mentre si chiudeva
    // rimonterebbe la conversazione appena chiusa.
    nextGeneration();
    say(t('call.ended'));
    void refresh();
  }, [refresh, say, t, queueDebrief, nextGeneration]);

  /**
   * Scorciatoie: Maiusc+A per rispondere, Maiusc+C per chiudere.
   *
   * Durante una conversazione non si naviga per tab (sezione 7bis), quindi
   * servono. Ma **non a tasto singolo**: un carattere solo, attivo su tutta la
   * pagina e non disattivabile, è una violazione di WCAG 2.1.4 — e qui `c`
   * premuto per sbaglio, o riconosciuto per errore da un comando vocale,
   * chiuderebbe una conversazione in corso senza chiedere niente a nessuno.
   * Il modificatore rende l'errore quasi impossibile e mantiene la scorciatoia
   * a portata di un gesto solo.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      // Si possono spegnere, dalla schermata delle notifiche: una scorciatoia
      // fatta di lettere — anche con Maiusc — ricade in WCAG 2.1.4, e il
      // criterio chiede di poterla disattivare. Qui non è teoria: Maiusc+C
      // chiude una conversazione in corso.
      if (!preferenceEnabled('console.shortcuts')) return;
      const target = event.target as HTMLElement | null;
      if (target?.isContentEditable) return;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (!event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) return;

      const key = event.key.toLowerCase();
      if (key === 'a' && incoming) {
        event.preventDefault();
        void answer();
      }
      if (key === 'c' && activeRef.current) {
        event.preventDefault();
        void hangUp();
      }
      /**
       * Maiusc+S: allerta il supervisore.
       *
       * "Ogni azione critica — rispondere, chiudere, **allertare** — ha una
       * scorciatoia da tastiera documentata, perché durante una chiamata non si
       * naviga per tab" (sezione 7bis). Era l'unica delle tre senza.
       */
      if (key === 's' && activeRef.current) {
        event.preventDefault();
        setEscalating(true);
      }
    };
    globalThis.addEventListener('keydown', onKey);
    return () => globalThis.removeEventListener('keydown', onKey);
  }, [answer, hangUp, incoming]);

  /**
   * Il focus segue la schermata che subentra.
   *
   * Le tre sezioni — turno, chiamata in arrivo, conversazione — si sostituiscono
   * a vicenda: senza questo, chi aveva il focus su un pulsante appena smontato
   * lo ritrova su `body` e deve ricominciare a tabulare, spesso proprio mentre
   * squilla (WCAG 2.4.3).
   */
  const headingRef = useRef<HTMLElement>(null);
  /**
   * Il titolo di pagina è **lo stato del turno**, e riceve due fuochi.
   *
   * Prima diceva "Console", cioè la stessa parola della voce di navigazione
   * accesa un centimetro più sopra, e sotto c'erano un saluto più grande di
   * qualunque titolo e una riga di stato: tre righe prima di qualunque cosa
   * utile. Adesso la prima riga risponde alla domanda per cui questa schermata
   * si apre — sono in ascolto? fino a quando? — e il saluto non c'è più: il
   * nome di chi è entrato sta già nella barra.
   *
   * Ci arriva il fuoco quando si torna qui dalla navigazione (`pageHeadingRef`,
   * che lo governa dalla radice) **e** quando la schermata del turno subentra a
   * una conversazione: sono due strade verso lo stesso posto, e il titolo dice
   * già dove si è finiti. Le altre tre sezioni hanno il proprio titolo e
   * `headingRef`.
   */
  const pageTitleRef = useRef<HTMLHeadingElement>(null);
  const setPageTitle = useCallback(
    (node: HTMLHeadingElement | null): void => {
      pageTitleRef.current = node;
      if (pageHeadingRef) pageHeadingRef.current = node;
    },
    [pageHeadingRef],
  );
  const section =
    incoming && !active ? 'incoming' : active ? 'active' : debriefFor ? 'debrief' : 'shift';
  /**
   * E **quale** debriefing, non solo "un debriefing".
   *
   * La sezione da sola non basta da quando i debriefing sono una coda: fra
   * quello della prima conversazione e quello della seconda `section` resta
   * `debrief` in tutte e due, quindi il fuoco non si spostava. Il pulsante
   * appena premuto esce dal documento — il componente si rimonta, la chiave è
   * la sessione — e il fuoco cadeva su `body`: chi naviga da tastiera doveva
   * ritabulare dall'inizio per trovare la schermata nuova, e chi non vede non
   * incontrava mai la riga che dice che questa conversazione era rimasta
   * indietro (WCAG 2.4.3).
   */
  const focusOn = `${section}:${debriefFor ?? ''}`;
  const previousSection = useRef(focusOn);
  useEffect(() => {
    if (previousSection.current === focusOn) return;
    previousSection.current = focusOn;
    // Fuori dalle tre sezioni che si sostituiscono, il titolo della schermata
    // è il titolo di pagina: è lì che si torna finita una conversazione.
    (section === 'shift' ? pageTitleRef.current : headingRef.current)?.focus();
  }, [focusOn, section]);

  /**
   * Il suono, finché la chiamata è lì.
   *
   * Squilla solo quando c'è una proposta e non c'è già una conversazione in
   * corso: durante una chiamata un suono coprirebbe la voce di chi sta
   * parlando. Ha sempre un equivalente visivo — il riquadro e l'annuncio
   * `assertive` ci sono comunque — perché un volontario sordo deve poter
   * lavorare con il suono spento (sezione 7bis).
   */
  /**
   * Quale chiamata sta facendo squillare, se ce n'è una.
   *
   * Un **id**, non gli oggetti: l'interrogazione periodica ricrea `incoming` e
   * `active` ogni due secondi, e con gli oggetti nella lista delle dipendenze
   * l'effetto si smontava e rimontava a ogni giro — lo squillo ripartiva da
   * capo e la ripetizione a 2,2 secondi non arrivava mai a scattare. È la
   * stessa trappola del token media, poche righe più su, dove il commento la
   * nomina già.
   */
  const ringingFor = incoming && !active ? incoming.sessionId : undefined;
  useEffect(() => {
    if (ringingFor && soundEnabled()) startRinging();
    else stopRinging();
    return stopRinging;
  }, [ringingFor]);

  /**
   * Schermo acceso per la durata del turno, e non un minuto di più.
   *
   * Su un telefono lo schermo si spegne dopo trenta secondi e la scheda viene
   * sospesa: il suono non parte e l'interrogazione rallenta. Fuori turno il
   * blocco si rilascia, perché tenere acceso lo schermo di chi non è in
   * ascolto è batteria presa senza motivo.
   */
  const onShift = Boolean(state?.shift);
  useEffect(() => {
    if (onShift) void requestWakeLock();
    else void releaseWakeLock();
    /**
     * Nessuna pulizia allo smontaggio.
     *
     * Andare in "Notifiche" o "I miei turni" non finisce il turno: rilasciare
     * il blocco lì vorrebbe dire che lo schermo si spegne mentre si è ancora
     * in ascolto, e che la riga "schermo acceso durante il turno" dice "non
     * attivo" proprio nella schermata che la descrive. Si rilascia quando il
     * turno finisce, e alla chiusura della scheda lo fa il browser.
     */
  }, [onShift]);

  /**
   * Screening del microfono, prima del turno (mock 2, `v-perm`).
   *
   * **Informativo, non un cancello.** A differenza della presa visione del
   * protocollo — un obbligo che il server impone e che blocca davvero il
   * pulsante del turno — qui non c'è niente da imporre lato server: il
   * permesso del microfono è del browser, può cambiare da un istante
   * all'altro, e chi risponde al telefono fisso (sezione 5.4) potrebbe non
   * averne bisogno affatto. Bloccare "Entra in turno" per un dato che non
   * possiamo verificare con certezza sarebbe un secondo `protocolAck`
   * inventato per un problema tecnico, non per un obbligo — esattamente lo
   * stesso trattamento di "Prova notifica" qui sopra: si vede il problema, si
   * dice come risolverlo, non si spegne il pulsante (`docs/DECISIONI.md`,
   * BB5).
   *
   * La verifica **non intrusiva** (Permissions API, quando il browser la
   * supporta) parte da sola appena si arriva su questa schermata fuori turno:
   * non chiede mai il permesso, legge solo quello che il browser sa già. Se il
   * browser non la supporta (Safari, principalmente), resta `unknown` finché
   * non si preme il pulsante di verifica — che invece **chiede** il permesso,
   * ed è quindi un gesto scelto, non una sorpresa al caricamento della
   * pagina.
   */
  const [micStatus, setMicStatus] = useState<
    'unknown' | 'checking' | 'ok' | 'blocked' | 'unsupported'
  >('unknown');

  useEffect(() => {
    if (onShift) return;
    const permissions = (navigator as Navigator & { permissions?: Permissions }).permissions;
    if (!permissions?.query) return;

    let cancelled = false;
    let status: PermissionStatus | undefined;
    const apply = (): void => {
      if (cancelled || !status) return;
      setMicStatus(
        status.state === 'denied' ? 'blocked' : status.state === 'granted' ? 'ok' : 'unknown',
      );
    };

    void permissions
      .query({ name: 'microphone' as PermissionName })
      .then((result) => {
        if (cancelled) return;
        status = result;
        apply();
        status.onchange = apply;
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      if (status) status.onchange = null;
    };
  }, [onShift]);

  const checkMicrophone = async (): Promise<void> => {
    // Come "Prova notifica" qui sopra: un giro alla volta, senza disabilitare
    // il pulsante — quello toglierebbe il fuoco a chi lo ha appena premuto
    // (U2, `docs/DECISIONI.md`).
    if (micStatus === 'checking') return;
    setMicStatus('checking');
    const devices = navigator.mediaDevices;
    if (!devices?.getUserMedia) {
      setMicStatus('unsupported');
      return;
    }
    try {
      const stream = await devices.getUserMedia({ audio: true });
      // Serve solo a sapere se si può: non è la sessione della chiamata, e
      // tenerla aperta accenderebbe la spia del microfono fuori da una
      // conversazione vera.
      for (const track of stream.getTracks()) track.stop();
      setMicStatus('ok');
      say(t('console.mic_ok'));
      /**
       * La scheda si smonta (il gate è `micStatus !== 'ok'`) e il fuoco
       * tornerebbe su `body`: si porta dove la persona voleva arrivare,
       * come fa `acknowledge()` qui sopra per il gate del protocollo.
       */
      shiftButtonRef.current?.focus();
    } catch {
      setMicStatus('blocked');
      say(t('console.mic_blocked'));
    }
  };

  /**
   * "Prova notifica", qui e non solo nella schermata delle notifiche.
   *
   * "Il test di notifica va fatto al primo turno" (mock 2): va messo dove si
   * guarda al primo turno, cioè in questa schermata, e deve dire cosa fare
   * quando fallisce.
   */
  const [testing, setTesting] = useState(false);
  const runNotificationTest = async (): Promise<void> => {
    if (testing) return;
    // Il tocco su questo pulsante è anche un gesto: ne approfittiamo per
    // sbloccare l'audio, che altrimenti resterebbe muto al primo squillo.
    void unlockSound();
    setTesting(true);
    try {
      const { outcome } = await testNotification();
      const [message, remedy] = testMessage(outcome, t);
      say(remedy ? `${message} ${remedy}` : message);
    } catch (error) {
      const key = error instanceof ApiError ? error.messageKey : 'errors.unexpected';
      say(`${t('notifications.test_failed')} ${apiMessage(t, key)}`);
    } finally {
      setTesting(false);
    }
  };

  /**
   * Presa visione del protocollo di crisi.
   *
   * Non è una formalità: senza, chi entra in turno riceve un rifiuto dal
   * server. Qui la si registra e si rilegge lo stato, così il pulsante del
   * turno smette di essere una porta chiusa.
   */
  const acknowledge = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      await acknowledgeProtocol();
      say(t('protocol.acked'));
      await refreshProtocol();
      /**
       * La carta si smonta e il fuoco tornerebbe su `body`: si porta dove la
       * persona voleva arrivare, cioè al pulsante del turno.
       */
      shiftButtonRef.current?.focus();
    } catch (error) {
      const key = error instanceof ApiError ? error.messageKey : 'errors.unexpected';
      say(`${t('protocol.ack_failed')} ${apiMessage(t, key)}`);
    } finally {
      setBusy(false);
    }
  };

  const changePresence = async (status: PresenceStatus): Promise<void> => {
    setState((previous) => (previous ? { ...previous, presence: status } : previous));
    try {
      await setPresence(status);
    } catch {
      // Chi crede di essersi messo in pausa e continua a ricevere chiamate è
      // il caso peggiore: va detto, non ingoiato.
      say(t('console.presence_failed'));
    }
    void refresh();
  };

  /**
   * Le scorciatoie si spengono da qui, dove se ne vede l'effetto.
   *
   * Restano governate dalla stessa preferenza di dispositivo della schermata
   * delle notifiche — una sola verità, letta da tutti e due i posti — e
   * l'esito si dice, perché spegnere una scorciatoia non cambia niente a
   * schermo tranne la riga che la ricordava.
   */
  const toggleShortcuts = (): void => {
    const next = !shortcuts;
    setPreference('console.shortcuts', next);
    setShortcuts(next);
    say(next ? t('notifications.shortcuts_on') : t('notifications.shortcuts_off'));
  };

  /**
   * Come sarai avvisato, detto com'è davvero.
   *
   * Con la push attiva e il suono acceso valgono entrambi; con il suono spento
   * si dice che la chiamata resta visibile e annunciata, che è la verità e
   * serve a chi non sente; senza push si dice solo di tenere la pagina aperta.
   */
  const notifyHint = (): string => {
    if (!soundEnabled()) return t('console.ringing_muted');
    return pushState === 'on' ? t('console.notify_hint') : t('console.notify_hint_sound_only');
  };

  /** Stato del servizio, senza lasciare "Riapre ." quando l'orario non c'è. */
  const serviceLabel = (): string => {
    if (state?.service.isOpen) return t('console.service_open');
    const when = formatWeekdayTime(
      state?.service.opensAt,
      i18n.resolvedLanguage,
      user.tenant?.timezone,
    );
    return when ? t('console.service_closed', { when }) : t('console.service_closed_unknown');
  };

  /**
   * Entrare o uscire dal turno, con la direzione dichiarata.
   *
   * Il debriefing chiude il turno da una schermata in cui `onShift` è ancora
   * vero ma la conversazione è finita: dedurre la direzione dallo stato
   * corrente andava bene finché il pulsante era uno solo.
   */
  const toggleShiftTo = async (direction: 'on' | 'off'): Promise<void> => {
    /**
     * Questo è **il gesto** che sblocca l'audio (sezione 5.7).
     *
     * I browser tengono l'audio sospeso finché la persona non tocca qualcosa,
     * e "entra in turno adesso" è il tocco giusto: da lì in poi possono
     * arrivare chiamate. Va fatto **prima** di await, cioè ancora dentro il
     * gestore del clic: dopo, il browser non lo considera più un gesto.
     */
    if (direction === 'on') void unlockSound();
    setBusy(true);
    try {
      if (direction === 'off') {
        await endShift();
      } else {
        await startShift();
      }
      setNotice(undefined);
      setAnnouncement({ text: '', seq: 0 });
    } catch (error) {
      /**
       * Tre errori diversi, tre messaggi diversi.
       *
       * Chiudere il turno non fallisce mai per orario: dire "il servizio è
       * chiuso" a chi sta uscendo dopo una chiamata pesante lo lascia senza
       * sapere se è ancora in ascolto — e intanto continua a ricevere chiamate.
       * E chi non ha letto il protocollo deve leggere **quello**, non un
       * generico "non è riuscito" (sezione 5.9).
       */
      const code = error instanceof ApiError ? error.code : '';
      if (code === 'protocol_ack_required') {
        void refreshProtocol();
        say(t('protocol.ack_required'));
      } else {
        say(
          direction === 'off'
            ? t('console.shift_end_failed')
            : code === 'service_closed'
              ? serviceLabel()
              : t('console.shift_start_failed'),
        );
      }
    } finally {
      setBusy(false);
      void refresh();
    }
  };

  const toggleShift = (): Promise<void> => toggleShiftTo(onShift ? 'off' : 'on');

  /**
   * La prima riga della schermata, e non è "Console".
   *
   * Finché lo stato non è arrivato non si scrive **niente** di vero: dire "non
   * sei in turno adesso" mentre lo si sta chiedendo al server è affermare il
   * contrario di quello che potrebbe essere, nella riga che si legge per prima.
   * Al suo posto una barra grigia, e per chi ascolta la parola che dice cosa
   * sta succedendo — un titolo vuoto non ha nome.
   */
  const shiftTitle = (): React.ReactNode => {
    if (!state) {
      return (
        <>
          <span className="visually-hidden">{t('console.loading')}</span>
          <span className="skeleton skeleton--half" aria-hidden="true" />
        </>
      );
    }
    return onShift
      ? /*
           L'ora e basta: il turno finisce oggi, e chi lo sta facendo sa che
           giorno è. Ci stava il giorno della settimana, e la frase diceva "in
           turno fino alle mercoledì 23:00" (AQ4).
        */
        t('console.shift_active', {
          when: formatTime(state.shift?.endsAt, i18n.resolvedLanguage, user.tenant?.timezone),
        })
      : t('console.shift_none');
  };

  return (
    <main className="page page--console" id="main" tabIndex={-1}>
      {/*
        Il titolo e ciò che lo governa sulla stessa riga: lo stato del turno e
        lo stato del servizio sono la stessa domanda vista da due lati — posso
        ricevere chiamate adesso? — e occupavano due righe piene a testa, con
        un saluto in mezzo.
      */}
      <div className="section-head">
        <h2 ref={setPageTitle} tabIndex={-1}>
          {shiftTitle()}
        </h2>
        {/*
          Lo stato del servizio non è mai solo un colore, ed è annunciato:
          cambia di rado, quindi una regione live qui non disturba nessuno.
          Finché non è arrivato resta **vuota**: "il servizio è chiuso" era
          quello che si leggeva mentre lo si stava chiedendo, cioè una
          schermata che dichiara una cosa prima di saperla.
        */}
        <p className="muted" role="status">
          {state ? serviceLabel() : ''}
        </p>
      </div>

      {/* Regioni sempre montate: una regione live che nasce piena non parla. */}
      <Announcer
        polite={announcement.text}
        seq={announcement.seq}
        /*
          L'annuncio dice **quale canale**, e lo dice prima di tutto.
          Era fisso su "chiamata anonima" anche per video e chat, che un
          account ce l'hanno sempre: una cosa falsa, e per giunta priva
          dell'informazione per cui la riga del canale esiste. Con Maiusc+A si
          risponde senza guardare lo schermo, e un volontario cieco poteva
          accendersi la camera addosso senza saperlo.
        */
        assertive={incoming ? incomingAnnouncement(incoming.channel, t) : ''}
      />

      {notice ? <p className="banner">{notice}</p> : null}

      {/*
        Le nuove sessioni sono ferme, e chi è in turno deve saperlo.

        Ha lo stesso peso dell'avviso sugli OTP e viene prima: lì le chiamate
        arrivano ancora, qui non ne arriva nessuna. La conversazione in corso,
        se c'è, prosegue.

        La frase intera era di duecentotrentacinque caratteri e stava fra chi
        apre la console e la scheda del turno. Adesso la prima riga dice le due
        cose che cambiano qualcosa — non arrivano chiamate, quella in corso
        prosegue — e il resto, che riguarda chi apre il widget e chi va
        avvisato, sta dietro una porta che si apre. Non è testo tolto: è testo
        messo dopo, perché chi legge poco legge le prime righe (AQ5).
      */}
      {state?.newSessionsSuspended ? (
        <div className="banner banner--warn">
          <p>{t('console.new_sessions_suspended_short')}</p>
          <details className="more">
            <summary>{t('console.suspended_more')}</summary>
            <p>{t('console.new_sessions_suspended')}</p>
          </details>
        </div>
      ) : null}

      {/*
        Le registrazioni ferme si dicono a chi è in turno.
        Non è un avviso di sistema: è la risposta a "una persona mi ha scritto
        che non riesce a registrarsi". E dice anche la cosa che conta — le
        chiamate non sono toccate (sezione 5.2).
      */}
      {state && !state.registrations.open ? (
        <div className="banner banner--warn">
          <p>{t('console.otp_suspended_short')}</p>
          <details className="more">
            <summary>{t('console.suspended_more')}</summary>
            <p>{t('console.otp_suspended')}</p>
          </details>
        </div>
      ) : null}

      {/*
        I due numeri, sopra la scheda del turno e non in fondo alla pagina.

        Stavano sotto il pulsante del turno, cioè dopo tutto: "quante ne ho
        fatte oggi" e "quante ne stanno aspettando" si guardano prima di
        cominciare, non dopo aver finito. E finché non sono arrivati non
        scrivono `0`: uno zero dichiarato mentre si sta ancora chiedendo è una
        risposta falsa data nella forma di un dato (difetto trasversale 4).

        Non compaiono durante una chiamata: lì la schermata ha una cosa sola da
        fare, e un contatore che si aggiorna accanto a una persona in linea è
        rumore. Non aggiornano nessuna regione live (sezione 7bis).
      */}
      {section === 'shift' ? (
        <section className="card counters" aria-label={t('console.today_summary')}>
          <p>
            {/*
              L'etichetta sta **sopra** il numero e non davanti, quindi i due
              punti non servono più: erano l'unico segno di punteggiatura
              scritto nel componente, e in una riga a capo diventavano una riga
              che finisce con un due punti e non continua.
            */}
            <span className="muted">{t('console.today')}</span>
            {state ? state.sessionsToday : <CounterLoading label={t('console.loading')} />}
          </p>
          <p>
            <span className="muted">{t('console.queue')}</span>
            {state ? state.waiting : <CounterLoading label={t('console.loading')} />}
          </p>
        </section>
      ) : null}

      {incoming && !active ? (
        <IncomingCall
          call={incoming}
          headingRef={headingRef}
          onAnswer={() => void answer()}
          onDecline={() => void decline()}
        />
      ) : null}

      {/*
        Il canale con la supervisione sta **sopra** la conversazione, non al suo
        posto: la conversazione resta montata sotto e il media non si
        interrompe. Il supervisore non ci entra mai (vincolo 4).
      */}
      {active && escalating ? (
        <SupervisionPanel
          sessionId={active.sessionId}
          /*
            Chiudendo, il fuoco torna al pulsante che ha aperto il pannello.
            Senza, cadrebbe su `body` e bisognerebbe ritabulare tutta la console
            con una persona in linea — che è la ragione per cui la scorciatoia
            esiste (U2, X3).
          */
          onClose={() => {
            setEscalating(false);
            escalateButtonRef.current?.focus();
          }}
        />
      ) : null}

      {/*
        Il debriefing, dopo. Facoltativo, e con due uscite: si torna in ascolto
        o si chiude il turno, in entrambi i casi anche senza compilare niente
        (sezione 5.8).
      */}
      {section === 'debrief' && debriefFor ? (
        <Debrief
          /*
            La chiave è la sessione: senza, passando al debriefing successivo
            React riuserebbe lo stesso componente e le risposte date sulla
            conversazione di prima resterebbero nel modulo — cioè finirebbero
            attaccate alla conversazione sbagliata, che è la sola cosa che una
            sessione lascia dietro di sé (sezione 5.8).
          */
          key={debriefFor}
          sessionId={debriefFor}
          /* Ce n'è più di uno in coda: questo è quello rimasto indietro. */
          earlier={debriefQueue.length > 1}
          headingRef={headingRef}
          timezone={user.tenant?.timezone}
          onDone={() => {
            dropDebrief(debriefFor);
            /*
              Qui, e non alla fine della chiamata, torna disponibile: è
              questo il gesto esplicito con cui il volontario dice "sono di
              nuovo raggiungibile", non la fine della conversazione — che
              lascerebbe squillare la prossima mentre scrive ancora la nota
              della precedente (`docs/DECISIONI.md`). Ma solo se questo era
              l'**ultimo** rimasto in coda: con più di un debriefing arretrato
              (`pendingNotes`, un crash recuperato), uscire dal primo non deve
              già riaprire lo squillo mentre gli altri restano da scrivere.
            */
            if (debriefQueue.length <= 1) void changePresence('available');
          }}
          onEndShift={() => {
            dropDebrief(debriefFor);
            void toggleShiftTo('off');
          }}
        />
      ) : null}

      {active ? (
        <ActiveCall
          call={active}
          media={media}
          emergencyNumber={state?.emergency[0]?.number}
          onEscalate={() => setEscalating(true)}
          escalateButtonRef={escalateButtonRef}
          /*
            Il fuoco entra nella conversazione che è appena cominciata.
            Senza, "Rispondi" spariva sotto le dita e il punto di lettura
            tornava a inizio documento: chi non guarda lo schermo sentiva
            "collegamento in corso" senza sapere dove si trova, e per arrivare
            a "disattiva il microfono" doveva ritabulare tutto — con una
            persona già in linea. È R6 e U2, sulla transizione che conta di
            più.
          */
          headingRef={headingRef}
          onEnd={() => void hangUp()}
        />
      ) : null}

      {/*
        Senza presa visione non si entra in turno (sezione 5.9). Il protocollo
        si legge qui, e il pulsante del turno resta spento finché non è letto:
        un pulsante che fallisce al tocco è peggio di un pulsante che dice
        perché non si può ancora premere.
      */}
      {section === 'shift' && protocol && !protocol.acknowledged ? (
        <section className="card card--accent" aria-labelledby="protocol-gate-heading">
          {/* `h3`: il titolo di pagina è uno solo, e le schede stanno sotto. */}
          <h3 id="protocol-gate-heading">{t('protocol.title')}</h3>
          <p className="banner banner--warn">{t('protocol.ack_required')}</p>
          {protocol.protocol ? (
            <ol className="protocol-steps">
              {protocol.protocol.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          ) : null}
          {/* Cambia etichetta e non si disabilita: X3. */}
          <button
            type="button"
            className="button button--primary button--wide"
            ref={protocolAckRef}
            onClick={() => void acknowledge()}
          >
            {t(busy ? 'protocol.acking' : 'protocol.ack')}
          </button>
        </section>
      ) : null}

      {/*
        Il microfono, prima di entrare in turno. Informativo: dice il
        problema e come risolverlo, non spegne "Entra in turno" (BB5).
      */}
      {section === 'shift' && !onShift && micStatus !== 'ok' ? (
        /*
          Il bordo colorato è `--accent` solo finché non c'è già un banner
          d'avviso dentro: bloccato, il banner arancione (`banner--warn`,
          qui sotto) è già il segnale di "serve un'azione", e un secondo
          bordo viola accanto a lui — più il pulsante secondario, anche lui
          viola — impilava tre tinte scorrelate nella stessa scheda, senza
          che nessuna delle tre spiegasse le altre (revisione UI, BQ1).
        */
        <section
          className={`card${micStatus === 'blocked' ? '' : ' card--accent'}`}
          aria-labelledby="mic-check-heading"
        >
          <div className="section-head">
            <div className="section-head__title">
              <h3 id="mic-check-heading">{t('console.mic_title')}</h3>
              <InfoTip>{t('console.mic_hint')}</InfoTip>
            </div>
          </div>
          {micStatus === 'blocked' ? (
            <>
              <p className="banner banner--warn">{t('console.mic_blocked')}</p>
              <ol className="protocol-steps">
                <li>{t('console.mic_step_1')}</li>
                <li>{t('console.mic_step_2')}</li>
                <li>{t('console.mic_step_3')}</li>
              </ol>
            </>
          ) : null}
          <button type="button" className="button" onClick={() => void checkMicrophone()}>
            {/* Cambia etichetta e non si disabilita: X3, U2. */}
            {t(
              micStatus === 'checking'
                ? 'console.mic_checking'
                : micStatus === 'blocked'
                  ? 'console.mic_retry'
                  : 'console.mic_verify',
            )}
          </button>
        </section>
      ) : null}

      {/*
        La scheda del turno, senza un titolo suo.
        Il titolo ce l'ha già: è la prima riga della pagina, e ripeterlo qui
        sarebbe la seconda intestazione che dice la stessa cosa della prima.
        Dentro, i gruppi si nominano da soli — la `legend` dello stato, le
        etichette dei pulsanti.
      */}
      {section === 'shift' ? (
        <div className="card stack">
          {onShift ? (
            <>
              {/*
                Lo stato è il comando più usato della console (sezione 5.4), e
                fuori turno non c'è niente da scegliere: prima stava lì
                comunque, disabilitato e con nessuna delle tre opzioni accesa —
                un comando spento in cima alla schermata di chi deve ancora
                entrare in turno.
              */}
              <PresencePicker
                value={state?.presence ?? 'offline'}
                disabled={busy}
                onChange={(status) => void changePresence(status)}
              />

              {/*
                Due azioni, due pesi, una riga sola.
                Erano due pulsanti larghi quanto lo schermo, uno sotto l'altro:
                "prova notifica" — che si tocca una volta, al primo turno —
                aveva esattamente la stessa forma di "chiudi il turno", che
                smette di far arrivare chiamate.
              */}
              <div className="actions">
                <button
                  type="button"
                  className="button"
                  ref={shiftButtonRef}
                  onClick={() => void toggleShift()}
                  disabled={busy}
                >
                  {t('console.shift_end')}
                </button>
                {/*
                  "Il test di notifica va fatto al primo turno" (mock 2): sta
                  dove si guarda al primo turno. Cambia etichetta e non si
                  disabilita: un pulsante che si disabilita mentre lavora perde
                  il fuoco, e chi naviga da tastiera si ritrova a inizio pagina
                  proprio mentre aspetta di leggere l'esito (U2). Il doppio
                  tocco lo ferma la guardia dentro il gestore.
                */}
                <button
                  type="button"
                  className="button button--quiet"
                  onClick={() => void runNotificationTest()}
                >
                  {t(testing ? 'notifications.testing' : 'notifications.test')}
                </button>
              </div>

              <p className="muted">
                {state && state.colleagues.length > 0
                  ? t('console.colleagues', { names: state.colleagues.join(', ') })
                  : t('console.colleagues_none')}
              </p>
            </>
          ) : (
            <>
              {state?.nextShift ? (
                <p className="muted">
                  {t('console.shift_next', {
                    when: formatWeekdayTime(
                      state.nextShift.startsAt,
                      i18n.resolvedLanguage,
                      user.tenant?.timezone,
                    ),
                  })}
                </p>
              ) : null}

              <button
                type="button"
                className="button button--primary button--big"
                ref={shiftButtonRef}
                onClick={() => void toggleShift()}
                disabled={busy}
              >
                {t('console.shift_start')}
              </button>
              {/*
                "Puoi metterti in ascolto anche fuori dai turni prenotati": è la
                risposta a una domanda che si fa **prima** di entrare in turno, e
                spiega il pulsante lì sopra. A chi è già in turno non serve, e in
                quella schermata era una riga in più fra il pulsante e le cose che
                cambiano — chi legge poco legge le prime, non le ultime (AQ5).
              */}
              <p className="muted">{t('console.shift_hint')}</p>
            </>
          )}

          {/*
            Se le notifiche non sono attive su questo dispositivo, si dice qui
            e si offre dove sistemarlo: scoprirlo mentre squilla è troppo
            tardi, e la console continua comunque a controllare da sola.

            Il rimedio sta **dentro** l'avviso, non sotto come terzo pulsante a
            tutta larghezza: è un pulsante vero e non un collegamento dentro la
            frase — dentro un paragrafo sarebbe alto quanto il testo, cioè
            sotto i 44 pixel che questa console si è data (7bis).
          */}
          {pushState && pushState !== 'on' ? (
            <div className="banner">
              <p>{t('console.notify_off')}</p>
              {onOpenNotifications ? (
                <div className="actions">
                  <button type="button" className="button" onClick={onOpenNotifications}>
                    {t('console.notify_settings')}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}

          {/*
            Le due righe che spiegano invece di cambiare.

            Sotto i pulsanti se ne accumulavano quattro: colleghi in ascolto,
            come vieni avvisato, le tre scorciatoie, lo stato delle notifiche.
            La prima resta a vista — è l'unica che cambia da sola, e alle undici
            di sera "sei l'unica persona in ascolto" è un'informazione, non una
            spiegazione. Le altre due si aprono, e con loro l'interruttore delle
            scorciatoie: chi le ha spente non trovava più nessuna riga che ne
            parlasse, quindi non aveva nessun modo di riaccenderle da qui.
          */}
          <details className="more">
            <summary>{t('console.more')}</summary>
            <p className="muted">{notifyHint()}</p>
            <div className="settings__row">
              <span className="settings__label">{t('notifications.shortcuts_label')}</span>
              <span className="settings__state">
                {shortcuts ? t('console.keyboard_hint') : t('notifications.shortcuts_off')}
              </span>
              <button type="button" className="button button--quiet" onClick={toggleShortcuts}>
                {t(
                  shortcuts ? 'notifications.shortcuts_disable' : 'notifications.shortcuts_enable',
                )}
              </button>
            </div>
          </details>
        </div>
      ) : null}
    </main>
  );
}

/**
 * Un numero che non è ancora arrivato.
 *
 * Non `0`: uno zero mostrato mentre la richiesta è in volo è una risposta
 * inventata, ed è quella che si legge per prima. Per chi guarda una barra
 * grigia, per chi ascolta la parola che dice che si sta caricando — il
 * segnaposto è `aria-hidden`, altrimenti sarebbe un nodo senza nome dentro un
 * riquadro che ne ha uno.
 */
function CounterLoading({ label }: { label: string }) {
  return (
    <>
      <span className="visually-hidden">{label}</span>
      <span className="skeleton skeleton--half" aria-hidden="true" />
    </>
  );
}

/**
 * Cosa si sente quando arriva una chiamata.
 *
 * Il canale prima della scorciatoia, e la scorciatoia solo se è accesa (X12).
 */
function incomingAnnouncement(channel: string, t: (key: string) => string): string {
  const which =
    channel === 'video'
      ? 'incoming.announce_video'
      : channel === 'chat'
        ? 'incoming.announce_chat'
        : 'incoming.announce_audio';
  const shortcut = preferenceEnabled('console.shortcuts')
    ? ` ${t('incoming.announce_shortcut')}`
    : '';
  return `${t(which)}${shortcut}`;
}
